import React, { useEffect } from 'react'
import { faClose } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import ReactModal from 'react-modal'
import {
  Button,
  FormControlLabel,
  FormHelperText,
  Grid,
  Switch,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { Store } from '../../../../../utils/toast'
import './CategoriesModal.scss'
import {
  useCreateCategoryMutation,
  useEditCategoryMutation,
} from '../../../../../store/api/categoriesApi'

import { ICategory } from '../../../../../types/categories'

type Props = {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  categories?: ICategory[]
  data?: ICategory
  edit?: boolean
}
const c = 'categories-modal'

const CategoriesModal = (props: Props) => {
  const { t } = useTranslation()
  const [createCategory] = useCreateCategoryMutation()
  const [editCategory] = useEditCategoryMutation()

  const {
    register,
    getValues,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm()

  function submit() {
    const params = {
      name: getValues().name,
      ru_name: getValues().ru_name,
      isNew: getValues().isNew || false,
      specialOfferEnd:
        getValues().hasTimer && getValues().specialOfferDate
          ? new Date(
              `${getValues().specialOfferDate}T${getValues().specialOfferTime || '23:59'}`,
            ).toISOString()
          : undefined,
    }

    if (props.data) {
      editCategory({ params, id: props.data._id })
        .unwrap()
        .then(() => {
          Store.addNotification({
            title: 'SUCCESS',
            message: 'Success',
            container: 'top-center',
            type: 'success',
            insert: 'top',
            animationIn: ['animate__animated', 'animate__fadeIn'],
            animationOut: ['animate__animated', 'animate__fadeOut'],
            dismiss: {
              duration: 1000,
              onScreen: true,
            },
          })
        })
        .catch(() => {
          Store.addNotification({
            title: 'ERROR',
            message: 'Error',
            container: 'top-center',
            type: 'danger',
            insert: 'top',
            animationIn: ['animate__animated', 'animate__fadeIn'],
            animationOut: ['animate__animated', 'animate__fadeOut'],
            dismiss: {
              duration: 1000,
              onScreen: true,
            },
          })
        })
        .finally(() => {
          props.setIsOpen(false)
        })
    } else {
      createCategory(params)
        .unwrap()
        .then(() => {
          Store.addNotification({
            title: 'SUCCESS',
            message: 'Success',
            container: 'top-center',
            type: 'success',
            insert: 'top',
            animationIn: ['animate__animated', 'animate__fadeIn'],
            animationOut: ['animate__animated', 'animate__fadeOut'],
            dismiss: {
              duration: 1000,
              onScreen: true,
            },
          })
        })
        .catch(() => {
          Store.addNotification({
            title: 'ERROR',
            message: 'Error',
            container: 'top-center',
            type: 'danger',
            insert: 'top',
            animationIn: ['animate__animated', 'animate__fadeIn'],
            animationOut: ['animate__animated', 'animate__fadeOut'],
            dismiss: {
              duration: 1000,
              onScreen: true,
            },
          })
        })
        .finally(() => {
          props.setIsOpen(false)
        })
    }
  }

  useEffect(() => {
    if (props.data) {
      setValue('name', props.data.name)
      setValue('ru_name', props.data?.ru_name)
      setValue('isNew', props.data?.isNew || false)
      setValue('hasTimer', !!props.data?.specialOfferEnd)
      if (props.data?.specialOfferEnd) {
        const d = new Date(props.data.specialOfferEnd)
        const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000)
          .toISOString()
          .slice(0, 16)
        setValue('specialOfferDate', local.slice(0, 10))
        setValue('specialOfferTime', local.slice(11, 16))
      }
    }
  }, [props.data])

  return (
    <ReactModal className='categories-modal-container' isOpen={props.isOpen}>
      <div className={c}>
        <div className={`${c}-close`}>
          <FontAwesomeIcon
            icon={faClose}
            onClick={() => props.setIsOpen(false)}
          />
        </div>
        <div className={`${c}-title`}>Categorie</div>
        <form onSubmit={handleSubmit(submit)} className={`${c}__content`}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                {...register('name', { required: true })}
                label='Nume'
                error={!!errors.name}
                fullWidth
                variant='outlined'
                size='small'
                inputProps={{
                  style: {
                    borderColor: 'red',
                  },
                }}
              />
              {errors?.name && (
                <FormHelperText
                  error={!!errors?.name}
                  id='component-error-text'
                >
                  {t('FIELD_REQUIRED')}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register('ru_name', { required: true })}
                label='Nume (ru)'
                error={!!errors.ru_name}
                fullWidth
                variant='outlined'
                size='small'
                inputProps={{
                  style: {
                    borderColor: 'red',
                  },
                }}
              />
              {errors?.ru_name && (
                <FormHelperText
                  error={!!errors?.ru_name}
                  id='component-error-text'
                >
                  {t('FIELD_REQUIRED')}
                </FormHelperText>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    {...register('isNew')}
                    checked={!!watch('isNew')}
                    onChange={(e) => setValue('isNew', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'var(--color-primary)',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        { backgroundColor: 'var(--color-primary)' },
                    }}
                  />
                }
                label='Categorie Nouă (afișează badge NEW)'
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    {...register('hasTimer')}
                    checked={!!watch('hasTimer')}
                    onChange={(e) => setValue('hasTimer', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#f97316',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track':
                        { backgroundColor: '#f97316' },
                    }}
                  />
                }
                label='Afișează timer ofertă specială'
              />
            </Grid>
            {watch('hasTimer') && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...register('specialOfferDate')}
                    label='Data expirării'
                    type='date'
                    fullWidth
                    variant='outlined'
                    size='small'
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    {...register('specialOfferTime')}
                    label='Ora expirării'
                    type='time'
                    fullWidth
                    variant='outlined'
                    size='small'
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              </>
            )}
          </Grid>
          <div className={`${c}__content-submit`}>
            <Button
              variant='contained'
              type='submit'
              sx={{
                backgroundColor: 'var(--color-primary)',
                textTransform: 'none',
                fontWeight: 'bold',
                width: '100%',
                '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
              }}
            >
              Salveaza
            </Button>
          </div>
        </form>
      </div>
    </ReactModal>
  )
}

export default CategoriesModal
